import React from 'react';
import classes from './TransactionItem.module.scss';
import { formatBalance, formatToken } from '@homeplay/utils';
import { DateTime } from 'luxon';

const getCampaignBonusTitle = (type, operationType) => {
  switch (type) {
    case 'campaign_bonus_allocation':
      return 'Bonus Allocated';
    case 'campaign_bonus_reservation':
      return 'Bonus Reserved';
    case 'campaign_bonus_awarding':
      switch (operationType) {
        case 'campaign_bonus:activation':
          return 'Bonus Awarded';
        case 'campaign_bonus:expiration':
          return 'Bonus Expired';
        case 'campaign_bonus:cancellation':
          return 'Bonus Cancelled';
        default:
          return 'Bonus Operation';
      }
    case 'campaign_bonus_release':
      switch (operationType) {
        case 'campaign_bonus:completion':
          return 'Bonus converted to Cash balance';
        case 'campaign_bonus:expiration':
          return 'Bonus Expiration Refund';
        case 'campaign_bonus:cancellation':
          return 'Bonus Cancellation Refund';
        default:
          return 'Bonus Operation';
      }
    case 'campaign_bonus_write_off':
      return 'Bonus Overlimit Deduction';
    default:
      return null;
  }
};

// NOTE: Same amounts as on hpui sites: bonus operations are counted in
// campaign_bonus_amount, a release moves money to the cash balance.
const getTransactionAmount = (
  type,
  amount,
  depositAmount,
  bonusAmount,
  campaignBonusAmount
) => {
  switch (type) {
    case 'campaign_bonus_allocation':
    case 'campaign_bonus_awarding':
    case 'campaign_bonus_write_off':
      return Number(campaignBonusAmount) || 0;
    case 'campaign_bonus_reservation':
      return -Math.abs(Number(campaignBonusAmount) || 0);
    case 'campaign_bonus_release':
      return (Number(amount) || 0) + (Number(depositAmount) || 0);
    default:
      return (
        (Number(amount) || 0) +
        (Number(depositAmount) || 0) +
        (Number(bonusAmount) || 0)
      );
  }
};

const TransactionItem = ({
  type,
  operationType,
  amount,
  depositAmount,
  bonusAmount,
  campaignBonusAmount,
  timestamp,
  detail,
  status,
}) => {
  let icon;
  let title;
  let details;
  const isLoad =
    type === 'load_gs_ticket' ||
    type === 'load_acoin' ||
    type === 'load_acoin_by_phone' ||
    type === 'im_withdrawal_cancellation' ||
    type === 'eft_withdrawal_cancellation' ||
    type === 'unload_gs_ticket_rollback' ||
    type === 'acquire_jackpot_win' ||
    type === 'balance_adjustment' ||
    type === 'campaign_bonus_allocation' ||
    type === 'campaign_bonus_release' ||
    (type === 'campaign_bonus_awarding' &&
      operationType === 'campaign_bonus:activation');
  const dateTime = DateTime.fromMillis(parseInt(timestamp)).toFormat(
    'dd.MM.yyyy HH:mm:ss'
  );

  if (isLoad) {
    icon = (
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M4.8487 22.1205L2.04586 16.3135L0 17.313L2.80458 23.1199L4.8487 22.1205ZM10.1005 6.83248H7.68575V4.40321H5.57145V6.83248H3.15725V8.95955H5.57145V11.3888H7.68575V8.95955H10.1005V6.83248ZM12.4579 6.05927C14.502 6.83422 16.6012 6.86729 18.7666 6.05115V7.03086C16.585 7.83076 14.4921 7.72693 12.4579 7.03086V6.05927ZM18.7666 4.97746C16.585 5.77736 14.4921 5.67063 12.4579 4.97456V3.76804C13.241 4.16828 14.3564 4.41829 15.5948 4.41829C16.8512 4.41829 17.9818 4.16016 18.7666 3.75006V4.97746ZM12.4579 8.10977C14.502 8.88472 16.6012 8.91721 18.7666 8.10165V9.25654C16.585 10.0576 14.4921 9.94913 12.4579 9.25306V8.10977ZM12.4579 10.3325C14.502 11.1087 16.6012 11.14 18.7666 10.3244V11.4608C16.5803 12.3523 14.4904 12.2288 12.4579 11.4608V10.3325ZM11.3355 12.0559C14.0745 13.502 16.9197 13.5826 19.889 12.0559V2.6816C19.889 0.727965 17.6355 0.5 15.5966 0.5C13.5577 0.5 11.3372 0.688521 11.3372 2.76861L11.3355 12.0559ZM15.5948 3.42001C11.4329 3.42001 11.278 1.36661 15.5948 1.36661C19.9412 1.36661 19.7747 3.42001 15.5948 3.42001ZM11.6156 16.7729C12.9962 16.8582 14.4057 16.9614 15.7874 16.9748C16.4365 16.8878 17.3402 14.9573 15.8454 14.8181C14.5612 14.6981 13.5397 15.6882 11.6412 15.596C9.97524 15.5125 8.49261 14.4243 7.64747 14.332C6.57553 14.216 5.70428 14.9591 4.92758 15.5995C4.70658 15.7799 4.38813 16.1012 4.38813 16.1012L6.84815 20.425C8.59483 20.3737 10.343 20.4279 12.0832 20.5874C12.9173 20.6663 16.0461 21.0259 16.7671 20.6088C17.7283 20.0508 20.9499 18.3797 21.487 17.7051C21.8049 17.3048 23.1506 13.7758 22.9256 13.4504C22.8247 13.3048 22.1622 13.1238 21.6303 13.4277C20.7707 13.9185 20.6628 16.0113 19.1407 17.1192C18.1767 17.8217 17.4359 17.9806 15.8454 17.9893C14.2549 17.998 11.5368 17.8234 11.3703 17.7874C10.7769 17.6575 10.8482 16.6204 11.6133 16.7729H11.6156ZM17.3141 16.835C19.1123 16.3872 19.4957 15.1198 20.1726 13.6116C20.406 13.079 20.8321 12.6543 21.3654 12.4226C21.8988 12.1909 22.5 12.1693 23.0486 12.3622C23.5172 12.5252 24.0747 12.8842 23.9917 13.6064C23.892 14.4829 22.8496 17.6726 22.2806 18.3669C21.7585 19.0003 17.603 21.4198 17.0351 21.6152C15.5084 22.1442 12.0234 21.6338 10.3366 21.4923C9.30875 21.4053 6.23561 21.4609 6.23561 21.4609L3.11955 15.9852C3.94497 14.864 6.18631 13.0635 7.65327 13.2787C9.33021 13.5246 10.2943 14.6603 12.3222 14.5733C13.7955 14.5113 16.0548 12.8285 17.3715 14.462C17.998 15.2381 17.8182 16.0937 17.3135 16.8367L17.3141 16.835ZM2.0679 17.4933C2.03109 17.4167 1.97948 17.3481 1.91604 17.2914C1.8526 17.2348 1.77859 17.1913 1.69826 17.1633C1.61794 17.1354 1.53288 17.1236 1.44799 17.1287C1.3631 17.1338 1.28005 17.1556 1.20362 17.1929C1.0486 17.2686 0.929928 17.4027 0.873608 17.5658C0.817288 17.7289 0.827911 17.9077 0.903149 18.063C0.939954 18.14 0.991684 18.209 1.05534 18.2658C1.11899 18.3227 1.19331 18.3664 1.27397 18.3944C1.35464 18.4223 1.44005 18.434 1.52525 18.4287C1.61046 18.4233 1.69376 18.4012 1.77033 18.3634C1.92463 18.2869 2.04246 18.1526 2.09819 17.9896C2.15391 17.8267 2.14303 17.6483 2.0679 17.4933Z"
          fill="#963D8E"
        />
      </svg>
    );
  } else {
    icon = (
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M9.96385 13.2182C9.96385 13.2182 9.96385 8.93381 10.0011 8.49012C10.0971 7.34938 11.4192 7.24971 11.379 8.80592C11.3634 9.37509 11.1628 10.6035 11.6455 12.0564C12.1283 13.5094 12.6062 13.9525 12.6302 14.3721C12.6974 15.6564 12.0796 18.3779 11.7806 19.7438L6.60526 19.7354C6.60526 19.7354 4.60897 15.0524 4.5129 14.1764C4.41684 13.3004 4.49789 8.4613 4.58015 8.12328C4.73745 7.47246 5.72869 6.19483 6.21861 5.60885V13.4427H8.99782C9.17193 13.908 9.8984 13.8114 9.96385 13.2182ZM18.4522 23.001L21.5274 19.3242H19.5461V11.459H17.3583V19.3242H15.377L18.4522 23.001ZM5.91962 21.6315H12.5611V24H5.91962V21.6315ZM11.46 22.1412C11.5937 22.1404 11.7246 22.1792 11.8361 22.2529C11.9477 22.3266 12.0348 22.4317 12.0865 22.555C12.1382 22.6783 12.1522 22.8141 12.1266 22.9453C12.1009 23.0765 12.0369 23.1971 11.9427 23.2919C11.8484 23.3867 11.7281 23.4513 11.5971 23.4777C11.466 23.504 11.3301 23.4908 11.2065 23.4397C11.083 23.3887 10.9774 23.3021 10.9031 23.1909C10.8289 23.0798 10.7893 22.9491 10.7894 22.8154C10.7891 22.6371 10.8595 22.466 10.9852 22.3396C11.111 22.2132 11.2817 22.1418 11.46 22.1412ZM2.92727 0.00240162H19.5028C19.6207 0.00397697 19.7332 0.0518222 19.8161 0.135614C19.899 0.219406 19.9456 0.332427 19.9459 0.450293V2.73178C19.9463 2.85047 19.8996 2.96446 19.8161 3.04884C19.7326 3.13322 19.6191 3.18112 19.5004 3.18207H16.2115V13.4487H13.3897C13.9522 14.5036 13.5992 15.7903 13.3897 16.9094C13.1231 18.3569 12.5948 20.8119 12.5948 20.8119L5.91482 20.7591C5.91482 20.7591 4.8119 18.399 4.06021 16.3318C3.30852 14.2647 3.40518 13.8882 3.40518 13.6865C3.40518 12.0534 3.28511 9.35228 3.56249 7.8507C3.7462 6.84325 5.46872 4.81453 6.22041 4.0022V3.17907H2.92727C2.80827 3.17859 2.69432 3.1309 2.61046 3.04647C2.52659 2.96204 2.47966 2.84778 2.47998 2.72877V0.450293C2.47966 0.33129 2.52659 0.217026 2.61046 0.132596C2.69432 0.0481673 2.80827 0.000476859 2.92727 0V0.00240162ZM15.168 1.62346H7.26029V12.3927H8.93538C8.93538 11.1559 8.94018 8.35623 8.99542 7.96658C9.24518 6.17622 12.1144 6.1576 12.405 8.01101C12.4945 8.58018 12.3678 9.03708 12.405 10.3795C12.4236 11.0574 12.6452 11.7791 12.8925 12.3927H15.1674L15.168 1.62346Z"
          fill="#3B9CA8"
        />
      </svg>
    );
  }

  if (detail && (!status || status === 'completed')) {
    details = formatToken(detail);
  }

  switch (type) {
    case 'acquire_jackpot_win':
      title = 'Jackpot Win';
      break;
    case 'load_gs_ticket':
      if (status === 'in_progress') {
        title = 'Ticket loading in progress';
      } else {
        title = 'Ticket';
      }
      break;
    case 'unload_gs_ticket':
      if (status === 'completed') {
        title = 'Cashout to Ticket';
      } else if (status === 'in_progress') {
        title = 'Cashout to Ticket in progress';
      } else if (status === 'failed') {
        title = 'Cashout to Ticket requested';
      } else {
        title = 'Cashout';
      }
      break;
    case 'load_acoin':
      title = 'aCoin Voucher';
      break;
    case 'load_acoin_by_phone':
      title = 'aCoin Balance';
      break;
    case 'request_withdrawal':
      title = 'ATM Withdrawal requested';
      break;
    case 'request_eft_withdrawal':
      title = 'EFT Withdrawal requested';
      icon = (
        <svg
          width="24"
          height="30"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 23 26"
        >
          <path
            fill="#3B9CA8"
            d="M1.9 1.7H2a164.1 164.1 0 0 0 1.1 0v1.1l.2.1H3.6l.2.2c.4.2.7.5 1 .9a28.3 28.3 0 0 0 .1.4v.4L5 5H3.8v-.3l-.1-.1v-.1l-.5-.4h-.1V4h-.2a44.2 44.2 0 0 1-1 0h-.1l-.5.5a7 7 0 0 0 0 .2v.5a9 9 0 0 1 0 .3l.5.4A3.8 3.8 0 0 1 2 6H3l.2.1h.2l.2.1.2.1.4.3a2.5 2.5 0 0 1 .6.8 4.3 4.3 0 0 1 0 .2l.1.1V9a4.3 4.3 0 0 0 0 .2l-.1.2-.1.2-.3.3-.5.4-.1.1h-.1l-.2.1h-.2l-.1.1H3v1.3H1.9v-1.3h-.4l-.1-.1h-.1l-.2-.2c-.2 0-.4-.2-.5-.4l-.3-.3v-.2L0 9a2.7 2.7 0 0 0 0-.2v-.3L0 8.3h1.2v.4c.2.2.3.4.6.5H2l.2.1h1l.1-.1.5-.5V7.7a1 1 0 0 0-.5-.4H3l-.1-.1H1.6v-.1H1.2V7L1 6.9c-.2 0-.4-.3-.6-.5a2.3 2.3 0 0 1-.3-.7V5.4L0 5.1v-.7h.1A24.6 24.6 0 0 1 .3 4c0-.2.2-.3.3-.5l.6-.4.2-.1h.2l.3-.1V1.7Z"
          />
          <path
            stroke="#3B9CA8"
            d="m3 13.3 9.5-6.7 9.5 6.7H3Z"
            strokeWidth="1.2"
            strokeLinejoin="bevel"
          />
          <path
            stroke="#3B9CA8"
            strokeWidth="1.2"
            d="M2.6 23.4H21.4V25.1H2.6z"
          />
          <path
            stroke="#3B9CA8"
            strokeWidth="1.2"
            d="M3.6 21.5H20.5V23.2H3.6z"
          />
          <path
            fill="#3B9CA8"
            d="m5.8 13.3-1 8h3.5l-1-8H5.8ZM11.5 13.3l-1 8H14l-1-8h-1.5ZM17.3 13.3l-1 8h3.4l-1-8h-1.4ZM14 2.7l-.1.5.1-.5Zm6 7 .5-7-6.2 3 5.7 4ZM6.5 5l.4.4a4.6 4.6 0 0 1 .6-.4c.3-.3.8-.7 1.4-1 1.3-.8 3-1.3 5-.9l.2-1.1a8 8 0 0 0-5.8.9 10.4 10.4 0 0 0-2.2 1.7l.4.4Zm7.4-1.9c1.4.4 2.5 1.1 3.3 2l.9-.8c-1-1-2.3-2-4-2.3L14 3.2Z"
          />
        </svg>
      );
      break;
    case 'im_withdrawal_cancellation':
      title = 'ATM Withdrawal cancelled';
      break;
    case 'eft_withdrawal_cancellation':
      title = 'EFT Withdrawal cancelled';
      break;
    case 'unload_gs_ticket_rollback':
      title = 'Cashout to Ticket failed';
      break;
    case 'balance_adjustment':
      title = Number(bonusAmount) ? 'Bonus Adjustment' : 'Balance Adjustment';
      break;
    default:
      title = getCampaignBonusTitle(type, operationType) || 'Undefined';
  }

  return (
    <div
      className={`${classes.TransactionItem} ${isLoad ? classes.Load : classes.Unload}`}
    >
      <div className={classes.Icon}>{icon}</div>
      <div className={classes.Center}>
        <h5>{title}</h5>
        <time>{dateTime}</time>
      </div>
      <div className={classes.Right}>
        {details && <div className={classes.Ticket}>{details}</div>}
        <div className={classes.Amount}>
          {formatBalance(
            getTransactionAmount(
              type,
              amount,
              depositAmount,
              bonusAmount,
              campaignBonusAmount
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default TransactionItem;
